/**
 * AI media helpers
 * - Shopify-Original-Alt (data-media-alt) enthält "//AI" → "AI"-Label unten rechts
 * - Sichtbares alt-Attribut darf Product Title bleiben (wird nicht für die Erkennung gebraucht)
 * - Casual Download/Save von Bildern erschweren (kein harter Schutz)
 */
(function () {
  'use strict';

  var MARKER = /\/\/\s*AI\b/i;

  function cleanAlt(alt) {
    return String(alt || '')
      .replace(MARKER, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function aiSource(img) {
    if (!img) return '';
    // Check every available alt channel — section images often only have
    // the marker in the visible alt; product media use data-media-alt.
    var parts = [];
    var own = img.getAttribute('data-media-alt');
    if (own) parts.push(own);
    var host = img.closest('[data-media-alt]');
    if (host) {
      var hostAlt = host.getAttribute('data-media-alt');
      if (hostAlt) parts.push(hostAlt);
    }
    parts.push(img.getAttribute('alt') || '');
    return parts.join(' ');
  }

  function ensureHost(img) {
    var picture = img.closest('picture');
    var target = picture || img;
    var parent = target.parentElement;
    if (!parent) return null;

    if (parent.classList.contains('ai-label-host')) return parent;

    var mediaKids = 0;
    for (var i = 0; i < parent.children.length; i++) {
      var tag = parent.children[i].tagName;
      if (tag === 'IMG' || tag === 'PICTURE' || tag === 'VIDEO') mediaKids++;
    }

    if (mediaKids <= 1) {
      parent.classList.add('ai-label-host');
      return parent;
    }

    // Flex/grid rows with several media kids (e.g. the product-card media
    // scroller) must keep their DOM untouched: wrapping an image in a span
    // makes the span the (shrinkable) flex item — it collapses to 0×0 between
    // the `flex:0 0 auto` slides — and breaks `:first-child` selectors like
    // the PLP sneak-peek animation. Use the container itself as host; the
    // badge is then positioned in content (scroll) coordinates, see
    // positionBadge().
    var display = '';
    try { display = getComputedStyle(parent).display; } catch (e) {}
    if (display.indexOf('flex') !== -1 || display.indexOf('grid') !== -1) {
      parent.classList.add('ai-label-host');
      parent.setAttribute('data-ai-host-content', '1');
      return parent;
    }

    var wrap = document.createElement('span');
    wrap.className = 'ai-label-host ai-label-host--wrapped';
    parent.insertBefore(wrap, target);
    wrap.appendChild(target);
    return wrap;
  }

  // Badges are positioned against the actual image box, not the host box:
  // hosts like `.itc__card` (image + text) or full-slide background wrappers
  // are much bigger than the image, which parked the label in the section
  // wrapper instead of on the picture.
  var tracked = [];
  var repositionQueued = false;
  var badgeSeq = 0;

  function positionBadge(entry) {
    var img = entry.img;
    var host = entry.host;
    var badge = entry.badge;
    if (!badge.isConnected || !img.isConnected) return;

    var ir = img.getBoundingClientRect();
    // Image not rendered (e.g. the CSS-hidden mobile variant of a
    // desktop/mobile pair, or inside a closed drawer) → no badge.
    if (!ir.width || !ir.height) {
      badge.style.display = 'none';
      return;
    }
    // Explicit value (not '') so container rules like the search drawer's
    // `.product-card__media-scroll > *:not(:first-child){display:none}`
    // can't hide the badge when it sits directly in the scroll container.
    badge.style.display = 'inline-block';

    var hr = host.getBoundingClientRect();

    if (host.hasAttribute('data-ai-host-content')) {
      // Scroll-container host (product-card media strip): pin the badge's
      // bottom-right corner to the image's bottom-right corner in CONTENT
      // coordinates. The badge then scrolls along with its slide, so swipe/
      // snap and the sneak-peek animation need no reposition listener.
      var x = Math.round(ir.right - hr.left - (host.clientLeft || 0) + host.scrollLeft);
      var y = Math.round(ir.bottom - hr.top - (host.clientTop || 0) + host.scrollTop);
      badge.style.right = 'auto';
      badge.style.bottom = 'auto';
      badge.style.left = x + 'px';
      badge.style.top = y + 'px';
      badge.style.transform = 'translate(calc(-100% - .45rem), calc(-100% - .35rem))';
      return;
    }

    var right = Math.max(0, Math.round(hr.right - ir.right));
    var bottom = Math.max(0, Math.round(hr.bottom - ir.bottom));
    badge.style.right = right ? 'calc(.45rem + ' + right + 'px)' : '.45rem';
    badge.style.bottom = bottom ? 'calc(.35rem + ' + bottom + 'px)' : '.35rem';
  }

  function repositionAll() {
    if (repositionQueued) return;
    repositionQueued = true;
    requestAnimationFrame(function () {
      repositionQueued = false;
      for (var i = tracked.length - 1; i >= 0; i--) {
        if (!tracked[i].badge.isConnected || !tracked[i].img.isConnected) {
          tracked.splice(i, 1);
          continue;
        }
        positionBadge(tracked[i]);
      }
    });
  }

  var visibilityObserver = typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(repositionAll)
    : null;

  function attachAiLabel(img) {
    if (!img || img.nodeType !== 1 || img.tagName !== 'IMG') return;
    if (img.dataset.aiLabeled === '1') return;

    var source = aiSource(img);
    if (!MARKER.test(source)) return;

    img.dataset.aiLabeled = '1';

    // Sichtbares alt nur bereinigen, wenn der Marker dort wirklich steht
    var visibleAlt = img.getAttribute('alt') || '';
    if (MARKER.test(visibleAlt)) {
      img.setAttribute('alt', cleanAlt(visibleAlt));
    }

    var host = ensureHost(img);
    if (!host) return;
    // Shared hosts (content mode) can carry one badge PER AI image; classic
    // hosts keep the old "one badge per host" dedupe.
    var contentMode = host.hasAttribute('data-ai-host-content');
    var token = img.dataset.aiBadgeToken;
    if (!token) {
      token = String(++badgeSeq);
      img.dataset.aiBadgeToken = token;
    }
    var hasBadge = false;
    for (var bi = 0; bi < host.children.length; bi++) {
      var kid = host.children[bi];
      if (!kid.classList || !kid.classList.contains('ai-label')) continue;
      if (!contentMode || kid.getAttribute('data-ai-for') === token) {
        hasBadge = true;
        break;
      }
    }
    if (hasBadge) return;

    var badge = document.createElement('span');
    badge.className = 'ai-label';
    badge.setAttribute('data-ai-for', token);
    badge.textContent = 'AI';
    badge.setAttribute('aria-hidden', 'true');
    // Geometrie inline: Theme-Regeln wie `.content-box__background-media > *`
    // strecken Grid-Kinder auf 100% — inline styles gewinnen immer.
    badge.style.cssText =
      'position:absolute;top:auto;left:auto;right:.45rem;bottom:.35rem;' +
      'width:auto;height:auto;min-width:0;min-height:0;max-width:none;max-height:none;' +
      // nowrap: in content mode the badge sits at `left:` ≈ container width,
      // where shrink-to-fit would otherwise wrap "AI" into a vertical stack.
      'margin:0;padding:0;inset-inline-start:auto;transform:none;white-space:nowrap;';
    host.appendChild(badge);

    var entry = { img: img, host: host, badge: badge };
    tracked.push(entry);
    positionBadge(entry);
    img.addEventListener('load', repositionAll);
    if (visibilityObserver) visibilityObserver.observe(img);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('img').forEach(function (img) {
      attachAiLabel(img);
    });
  }

  function hardenImage(img) {
    if (!img || img.dataset.aiProtected === '1') return;
    img.dataset.aiProtected = '1';
    img.setAttribute('draggable', 'false');
  }

  function protectAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('img').forEach(hardenImage);
  }

  function onContextMenu(e) {
    var t = e.target;
    if (!t) return;
    if (t.closest && t.closest('img, picture, svg image, .ai-label-host')) {
      e.preventDefault();
    }
  }

  function onDragStart(e) {
    var t = e.target;
    if (!t) return;
    if (t.closest && t.closest('img, picture')) {
      e.preventDefault();
    }
  }

  function init() {
    scan(document);
    protectAll(document);

    window.addEventListener('resize', repositionAll);
    window.addEventListener('load', repositionAll);

    document.addEventListener('contextmenu', onContextMenu, { capture: true });
    document.addEventListener('dragstart', onDragStart, { capture: true });

    if (typeof MutationObserver === 'undefined') return;
    var obs = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes' && m.target && m.target.tagName === 'IMG') {
          attachAiLabel(m.target);
          hardenImage(m.target);
          continue;
        }
        if (!m.addedNodes) continue;
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (!node || node.nodeType !== 1) continue;
          if (node.tagName === 'IMG') {
            attachAiLabel(node);
            hardenImage(node);
          } else {
            scan(node);
            protectAll(node);
          }
        }
      }
      repositionAll();
    });
    obs.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['alt', 'src', 'data-media-alt']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
